/**
 * UTM & Traffic Source tracking.
 * - Reads URL params (utm_source, utm_medium, utm_campaign, utm_content, utm_term)
 * - Reads document.referrer for traffic source
 * - first_visit: saved in localStorage (never overwritten)
 * - last_visit: updated on every visit (localStorage)
 */

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const FIRST_KEY = "sm_utm_first";
const LAST_KEY = "sm_utm_last";

function readUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    for (const k of UTM_KEYS) {
        const v = params.get(k);
        if (v) utm[k] = v;
    }
    return utm;
}

function detectTrafficSource(referrer) {
    if (!referrer) return "direct";
    try {
        const host = new URL(referrer).hostname.replace(/^www\./, "");
        if (host.includes("facebook.com") || host.includes("fb.com") || host.includes("instagram.com") || host.includes("meta.com")) return "facebook";
        if (host.includes("google.")) return "google";
        if (host.includes("youtube.com")) return "youtube";
        if (host.includes("tiktok.com")) return "tiktok";
        if (host.includes("twitter.com") || host.includes("x.com")) return "twitter";
        if (host.includes("linkedin.com")) return "linkedin";
        if (host.includes("messenger.com")) return "messenger";
        if (host.includes("whatsapp.com") || host.includes("wa.me")) return "whatsapp";
        return "referral";
    } catch {
        return "referral";
    }
}

function buildVisitData() {
    const utm = readUrlParams();
    const referrer = document.referrer || "";
    return {
        ...utm,
        traffic_source: detectTrafficSource(referrer),
        landing_page: window.location.href,
        referrer,
        timestamp: new Date().toISOString(),
    };
}

/** Capture UTM on page load. Call once (e.g., in App or LandingPage mount). */
export function captureUtm() {
    try {
        const visit = buildVisitData();
        // First visit — never overwrite
        if (!localStorage.getItem(FIRST_KEY)) {
            localStorage.setItem(FIRST_KEY, JSON.stringify(visit));
        }
        // Last visit — always update
        localStorage.setItem(LAST_KEY, JSON.stringify(visit));
    } catch {
        /* storage may be unavailable (private mode) — tracking is optional */
    }
}

/** Get first-visit UTM data (or null). */
export function getFirstVisit() {
    try {
        const raw = localStorage.getItem(FIRST_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** Get last-visit UTM data (or null). */
export function getLastVisit() {
    try {
        const raw = localStorage.getItem(LAST_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

/** Build the UTM payload for order submission (first + last visit). */
export function buildUtmPayload() {
    const first = getFirstVisit();
    const last = getLastVisit();
    const current = buildVisitData();
    const hasUtm = (d) => d && Object.keys(d).some((k) => k.startsWith("utm_") && d[k]);

    return {
        // First-visit attribution (primary)
        utm_source: first?.utm_source || null,
        utm_medium: first?.utm_medium || null,
        utm_campaign: first?.utm_campaign || null,
        utm_content: first?.utm_content || null,
        utm_term: first?.utm_term || null,
        traffic_source: first?.traffic_source || last?.traffic_source || current.traffic_source || null,
        // Last-visit attribution
        last_utm_source: last?.utm_source || null,
        last_utm_medium: last?.utm_medium || null,
        last_utm_campaign: last?.utm_campaign || null,
        last_utm_content: last?.utm_content || null,
        last_utm_term: last?.utm_term || null,
        last_traffic_source: last?.traffic_source || null,
        // Page context
        landing_page: first?.landing_page || current.landing_page,
        referrer: first?.referrer || current.referrer,
        utm_note: hasUtm(first) || hasUtm(last) ? null : "No utm_* detected for this order",
    };
}